import './css/index.css';

import {
  initPostHog,
  PostHogPageTracker,
} from '@plerionhq/design-system/analytics';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';
import React, { Suspense, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { isHeadlessCapturePath } from 'authInterface/lib/headless-capture';
import { AuthenticatedRoutes } from './components/AuthenticatedRoutes/AuthenticatedRoutes';
import { IntegrationCallbackPage } from './components/IntegrationCallbackPage';
import { Progress } from './components/Progress/Progress';
import {
  APP_ENV_TYPE,
  APP_MONITOR_ID,
  APPLICATION_VERSION,
  AWS_REGION,
  ENDPOINT,
  GUEST_ROLE_ARN,
  IDENTITY_POOL_ID,
  SESSION_SAMPLE_RATE,
} from './constants/rumConfig';
import { OAUTH_CONSENT_PATH } from './constants/oauthConsentPath';
import { ContextualViewProvider } from './context/SlideOverContext/SlideOver';
import { ThemeProvider } from './context/ThemeContext/ThemeContext';
import { withErrorBoundary } from './hoc/withErrorBoundary';
import { PleriRenderWindow } from './components/Pleri/PleriRenderWindow';
import { ChameleonUpdater } from './components/utils/ChameleonUpdater';
import { ChameleonNavHook } from './components/utils/ChameleonNavHook';
import { initializeChameleon } from './lib/chameleon';

const initializeAwsRum = (): AwsRum | null => {
  try {
    const config: AwsRumConfig = {
      sessionSampleRate: SESSION_SAMPLE_RATE,
      guestRoleArn: GUEST_ROLE_ARN,
      identityPoolId: IDENTITY_POOL_ID,
      endpoint: ENDPOINT,
      telemetries: ['performance', 'errors', 'http'],
      allowCookies: true,
      enableXRay: false,
      disableAutoPageView: true,
    };
    return new AwsRum(APP_MONITOR_ID, APPLICATION_VERSION, AWS_REGION, config);
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * The signed-in chrome. Rendered as its own component so the decision can read
 * the router location: the component below owns BrowserRouter, and its
 * window.location is still the pre-navigation path when it renders.
 *
 * The auth interface owns the OAuth consent page, and every part of this
 * chrome is full height, so drawing any of it there leaves the consent card
 * below the fold.
 */
const AuthenticatedShell = ({
  isLoggedIn,
  awsRumInstance,
}: {
  isLoggedIn: boolean;
  awsRumInstance: AwsRum | null;
}) => {
  const location = useLocation();
  if (location.pathname.startsWith(OAUTH_CONSENT_PATH)) {
    return null;
  }
  if (!isLoggedIn && !isHeadlessCapturePath(location.pathname)) {
    return null;
  }
  return (
    <>
      <ChameleonUpdater />
      <ChameleonNavHook />
      <PleriRenderWindow>
        <AuthenticatedRoutes awsRumInstance={awsRumInstance} />
      </PleriRenderWindow>
    </>
  );
};

const AuthInterface = withErrorBoundary(
  React.lazy(() => import('authInterface/AuthInterface')),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Don't retry 401/403 (a retry just re-triggers the token refresh → loop); one retry otherwise.
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        return status === 401 || status === 403 ? false : failureCount < 1;
      },
    },
  },
});

console.info(`Container v${process.env.npm_package_version}`);

initPostHog({
  apiKey: process.env.POSTHOG_PUBLIC_KEY,
  host: process.env.POSTHOG_HOST,
});

export const App = (): React.ReactElement => {
  const awsRumInstance =
    APP_ENV_TYPE === 'production' ? initializeAwsRum() : null;

  useEffect(() => {
    initializeChameleon();
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <>
            <PostHogPageTracker />
            <ContextualViewProvider>
              <Root awsRumInstance={awsRumInstance} />
            </ContextualViewProvider>
          </>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

const Root = React.memo(function Root({
  awsRumInstance,
}: {
  awsRumInstance: AwsRum | null;
}) {
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  // TODO Remove this after we improve regional redirection properly
  const isIntegrationCallbackPage = window.location.pathname.startsWith(
    '/settings/integrations/add',
  );
  if (
    window.location.host === process.env.ROOT_APP_DOMAIN &&
    isIntegrationCallbackPage
  ) {
    window.location.reload();
  }

  return (
    <div className="main-app" id="main-app">
      <div className="min-h-screen bg-gray-100! antialiased dark:bg-gray-900!">
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            success: {
              iconTheme: {
                primary: '#218380',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#d64045',
                secondary: '#fff',
              },
            },
          }}
        />

        <Routes>
          <Route path="/callback/jira" element={<IntegrationCallbackPage />} />
          <Route
            path="/callback/clickup"
            element={<IntegrationCallbackPage />}
          />
          <Route path="/callback/vanta" element={<IntegrationCallbackPage />} />
          <Route
            path="/callback/linear"
            element={<IntegrationCallbackPage />}
          />
          <Route path="/callback/asana" element={<IntegrationCallbackPage />} />

          <Route
            path="*"
            element={
              <>
                <AuthenticatedShell
                  isLoggedIn={isLoggedIn}
                  awsRumInstance={awsRumInstance}
                />
                <Suspense fallback={<Progress />}>
                  <AuthInterface
                    setIsLoggedIn={setIsLoggedIn}
                    isLoggedIn={isLoggedIn}
                  />
                </Suspense>
              </>
            }
          />
        </Routes>
      </div>
    </div>
  );
});
